import { PROMO_VOUCHER_AI_INSTRUCTIONS } from '~/components/ContentTools/promoVoucherAiInstructions';

import {
  type CreateOrUpdatePromoPayload,
  type CreateVoucherPayload,
  type PreviewVoucherPayload,
  type UpdateVoucherPayload,
  type Voucher,
  checkPromoDraft,
  checkPromoRequest,
  checkVoucherCode,
  createOrUpdatePromo,
  createVoucher,
  getInternalPromo,
  getPromoMeta,
  getVoucher,
  listVouchers,
  previewVoucher,
  updateVoucher,
} from '~/services/PromoService';

import { createOpenAIClient } from '~/utils/openaiClient';

export type AiCodeKind = 'promo' | 'voucher';
export type AiCodeOperation = 'create' | 'update';
export type AiCodeContext = {
  kind: AiCodeKind;
  operation: AiCodeOperation;
  codeName: string;
  brand: CreateVoucherPayload['brand'];
  request: string;
};
type ExistingCode = { id: string; version: string; record: Record<string, unknown> };
export type AiCodeProposal = {
  context: AiCodeContext;
  summary: string;
  payload: CreateOrUpdatePromoPayload | CreateVoucherPayload | UpdateVoucherPayload;
  existing: ExistingCode | null;
  preview: unknown;
  warnings: Array<string>;
  errors: Array<string>;
  changes: Array<{ field: string; before: unknown; after: unknown }>;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

function promoVersion(record: Record<string, unknown>): string {
  return typeof record.updated_at === 'string' ? record.updated_at : '';
}

function voucherVersion(record: Record<string, unknown>): string {
  return typeof record.updatedAt === 'string' ? record.updatedAt : '';
}

export async function findExistingCode(
  kind: AiCodeKind,
  codeName: string,
  brand: CreateVoucherPayload['brand'],
): Promise<ExistingCode | null> {
  if (kind === 'promo') {
    try {
      const response = await getInternalPromo({ code_name: codeName, brand });
      const record = response.result as Record<string, unknown>;
      if (record.code_name !== codeName || record.brand !== brand || typeof record.id_promo_code !== 'string') {
        throw new Error('Promo identity did not match the requested code and brand.');
      }
      return { id: record.id_promo_code, version: promoVersion(record), record };
    } catch (error) {
      if (isObject(error) && error.status === 404) return null;
      throw error;
    }
  }

  const response = await listVouchers({ voucher_name: codeName, brand, only_active: false, page: 0, limit: 100 });
  const matches = response.result.vouchers.filter(
    (voucher) => voucher.voucherName === codeName && voucher.brand === brand,
  );
  if (matches.length > 1) throw new Error('Multiple vouchers matched this name and brand.');
  if (!matches[0]) return null;
  const detail = await getVoucher(matches[0].voucherId);
  const record = detail.result as unknown as Record<string, unknown>;
  if (record.voucherName !== codeName || record.brand !== brand) {
    throw new Error('Voucher identity did not match the requested code and brand.');
  }
  return { id: matches[0].voucherId, version: voucherVersion(record), record };
}

function voucherPreviewFromRecord(voucher: Voucher): PreviewVoucherPayload {
  const compatibility = voucher as Voucher & {
    voucher_rewards?: Voucher['voucherRewards'];
    voucher_conditions?: Voucher['voucherConditions'];
  };
  return {
    voucher_name: voucher.voucherName,
    brand: voucher.brand,
    voucher_title: voucher.voucherTitle,
    voucher_description: voucher.voucherDescription,
    voucher_call_to_action: voucher.voucherCallToAction,
    voucher_call_to_action_url: voucher.voucherCallToActionUrl,
    voucher_presentation_config: voucher.voucherPresentationConfig,
    voucher_breakdown_title: voucher.voucherBreakdownTitle,
    voucher_amount_display_override: voucher.voucherAmountDisplayOverride,
    voucher_conditions: voucher.voucherConditions ?? compatibility.voucher_conditions ?? [],
    voucher_rewards: voucher.voucherRewards ?? compatibility.voucher_rewards ?? [],
    voucher_toggles: voucher.voucherToggles,
    voucher_reporting_tags: voucher.voucherReportingTags,
    show_voucher_from_date: voucher.showVoucherFromDate,
    voucher_user_expires_at: voucher.voucherUserExpiresAt,
    voucher_user_expires_in_days: voucher.voucherUserExpiresInDays,
    theme: voucher.theme,
    product_type: voucher.productType,
    case_id: voucher.caseId,
    reason: voucher.reason,
    reward_kind: voucher.rewardKind,
  };
}

export function buildVoucherPreview(
  payload: CreateVoucherPayload | UpdateVoucherPayload,
  existing: ExistingCode | null,
): PreviewVoucherPayload {
  if (!existing) return payload as CreateVoucherPayload;
  const base = voucherPreviewFromRecord(existing.record as Voucher);
  const previewUpdates = Object.fromEntries(Object.entries(payload).filter(([field]) => field in base));
  return { ...base, ...previewUpdates } as PreviewVoucherPayload;
}

function changesForPayload(
  context: AiCodeContext,
  payload: CreateOrUpdatePromoPayload | CreateVoucherPayload | UpdateVoucherPayload,
  existing: ExistingCode | null,
): AiCodeProposal['changes'] {
  const current = existing
    ? context.kind === 'voucher'
      ? (voucherPreviewFromRecord(existing.record as Voucher) as Record<string, unknown>)
      : existing.record
    : {};
  const candidate = context.kind === 'promo' ? (payload as CreateOrUpdatePromoPayload) : payload;
  const entries =
    context.kind === 'promo'
      ? [
          ...Object.entries((candidate as CreateOrUpdatePromoPayload).promo).map(([field, after]) => ({
            field: `promo.${field}`,
            before: current[field],
            after,
          })),
          ...(['promoDiscounts', 'promoConditions'] as const)
            .filter((field) => field in candidate)
            .map((field) => ({
              field,
              before: current[field === 'promoDiscounts' ? 'promo_discounts' : 'promo_conditions'],
              after: candidate[field],
            })),
          ...(['promoDiscount', 'promoCondition'] as const)
            .filter((field) => field in candidate)
            .map((field) => ({
              field,
              before: current[field === 'promoDiscount' ? 'promo_discounts' : 'promo_conditions'],
              after: candidate[field],
            })),
        ]
      : Object.entries(candidate).map(([field, after]) => ({ field, before: current[field], after }));
  return entries.filter(({ before, after }) => JSON.stringify(before) !== JSON.stringify(after));
}

export function normalizeAiPayload(
  context: AiCodeContext,
  value: unknown,
  existing: ExistingCode | null,
): CreateOrUpdatePromoPayload | CreateVoucherPayload | UpdateVoucherPayload {
  if (!isObject(value) || Object.keys(value).length === 0) throw new Error('The AI did not return a write payload.');
  if (context.kind === 'promo') {
    if (!isObject(value.promo)) throw new Error('Promo proposal is missing its promo object.');
    if (value.promo.code_name && value.promo.code_name !== context.codeName) throw new Error('Promo name changed.');
    if (value.promo.brand && value.promo.brand !== context.brand) throw new Error('Promo brand changed.');
    if (context.operation === 'create' && (value.promo.id_promo_code || value.mode === 'update-only')) {
      throw new Error('Create proposal contains an existing promo ID or update mode.');
    }
    if (context.operation === 'update' && !existing) throw new Error('Promo no longer exists.');
    if (context.operation === 'update' && value.promo.id_promo_code && value.promo.id_promo_code !== existing?.id) {
      throw new Error('Promo ID changed.');
    }
    return {
      ...value,
      requestSource: 'admin-ai-workflow',
      mode: context.operation === 'create' ? 'create-only' : 'update-only',
      promo: {
        ...value.promo,
        code_name: context.codeName,
        brand: context.brand,
        ...(existing ? { id_promo_code: existing.id } : {}),
      },
    } as CreateOrUpdatePromoPayload;
  }
  if (value.voucher_name && value.voucher_name !== context.codeName) throw new Error('Voucher name changed.');
  if (value.brand && value.brand !== context.brand) throw new Error('Voucher brand changed.');
  if (value.user_id || value.voucher_id) throw new Error('Voucher assignment or ID fields are outside this workflow.');
  if (context.operation === 'create') {
    if (!Array.isArray(value.voucher_rewards) || !Array.isArray(value.voucher_toggles)) {
      throw new Error('Voucher creation requires rewards and toggles.');
    }
    return { ...value, voucher_name: context.codeName, brand: context.brand } as CreateVoucherPayload;
  }
  if (!existing) throw new Error('Voucher no longer exists.');
  const { voucher_name: _name, brand: _brand, ...update } = value;
  return update as UpdateVoucherPayload;
}

export async function prepareAiCodeProposal(context: AiCodeContext): Promise<AiCodeProposal> {
  const existing = await findExistingCode(context.kind, context.codeName, context.brand);
  if (context.operation === 'create' && existing) throw new Error('This code already exists for the selected brand.');
  if (context.operation === 'update' && !existing) throw new Error('No existing code matched this name and brand.');
  const metadata = await getPromoMeta();
  const meta = metadata.result;
  const relevantMeta =
    context.kind === 'promo'
      ? {
          promo_types: meta.promo_types,
          promo_toggles: meta.promo_toggles,
          promo_region_groupings: meta.promo_region_groupings,
          promo_product_types: meta.promo_product_types,
          promo_condition_types: meta.promo_condition_types,
          promo_tags: meta.promo_tags,
        }
      : {
          voucher_toggles: meta.voucher_toggles,
          voucher_condition_types: meta.voucher_condition_types,
          voucher_product_types: meta.voucher_product_types,
          voucher_themes: meta.voucher_themes,
          voucher_reward_kind_options: meta.voucher_reward_kind_options,
        };
  const response = await createOpenAIClient().responses.create({
    model: 'gpt-5.4',
    instructions: PROMO_VOUCHER_AI_INSTRUCTIONS,
    input: JSON.stringify({
      kind: context.kind,
      operation: context.operation,
      codeName: context.codeName,
      brand: context.brand,
      request: context.request,
      currentRecord: existing?.record ?? null,
      promoMeta: relevantMeta,
    }),
    text: { format: { type: 'json_object' } },
  });
  let output: unknown;
  try {
    output = JSON.parse(response.output_text);
  } catch (error) {
    throw new Error('The AI response was not valid JSON. Try generating again.', { cause: error });
  }
  if (!isObject(output)) throw new Error('The AI response was not a proposal.');
  const payload = normalizeAiPayload(context, output.payload, existing);
  const summary = typeof output.summary === 'string' ? output.summary : 'Review the proposed changes.';
  let preview: unknown = null;
  let warnings: Array<string> = [];
  let errors: Array<string> = [];
  if (context.kind === 'promo' && context.operation === 'create') {
    const check = await checkPromoDraft(payload as CreateOrUpdatePromoPayload);
    preview = check.result;
    warnings = check.result.promo_warnings
      .filter((warning) => warning.level !== 'error')
      .map((warning) => warning.message ?? warning.code);
    errors = check.result.promo_warnings
      .filter((warning) => warning.level === 'error')
      .map((warning) => warning.message ?? warning.code);
  } else if (context.kind === 'voucher') {
    const check = await previewVoucher(
      buildVoucherPreview(payload as CreateVoucherPayload | UpdateVoucherPayload, existing),
    );
    preview = check.result;
    warnings = (check.result.voucher.voucherWarnings ?? [])
      .filter((warning) => warning.level !== 'error')
      .map((warning) => warning.message);
    errors = (check.result.voucher.voucherWarnings ?? [])
      .filter((warning) => warning.level === 'error')
      .map((warning) => warning.message);
  }
  return {
    context,
    summary,
    payload,
    existing,
    preview,
    warnings,
    errors,
    changes: changesForPayload(context, payload, existing),
  };
}

export async function saveAiCodeProposal(
  proposal: AiCodeProposal,
): Promise<{ saved: unknown; check: unknown; mismatches: Array<string> }> {
  const { context, payload, existing } = proposal;
  if (proposal.errors.length > 0) {
    throw new Error('The pre-save check reported errors. Generate a new proposal.');
  }
  const latest = await findExistingCode(context.kind, context.codeName, context.brand);
  if (context.operation === 'create' && latest) throw new Error('Code name is now taken. Generate a new proposal.');
  if (
    context.operation === 'update' &&
    (!latest || latest.id !== existing?.id || latest.version !== existing.version)
  ) {
    throw new Error('The saved code changed during review. Generate a new proposal.');
  }
  let saved: unknown = null;
  let savedVoucherId = existing?.id;
  if (context.kind === 'promo') {
    await createOrUpdatePromo(payload as CreateOrUpdatePromoPayload);
  } else if (context.operation === 'create') {
    const created = await createVoucher(payload as CreateVoucherPayload);
    savedVoucherId = created.result.voucherId;
  } else {
    await updateVoucher(existing.id, payload as UpdateVoucherPayload);
  }
  const mismatches: Array<string> = [];
  try {
    saved =
      context.kind === 'promo'
        ? (await getInternalPromo({ code_name: context.codeName, brand: context.brand })).result
        : savedVoucherId
          ? (await getVoucher(savedVoucherId)).result
          : null;
    if (!saved) mismatches.push('Readback unavailable: missing saved voucher ID');
  } catch (error) {
    mismatches.push(`Readback unavailable: ${formatError(error)}`);
  }
  let check: unknown;
  try {
    check =
      context.kind === 'promo'
        ? await checkPromoRequest({ codeName: context.codeName, brand: context.brand })
        : await checkVoucherCode({ voucherName: context.codeName, brand: context.brand });
  } catch (error) {
    check = { error: formatError(error) };
    mismatches.push('Code check failed');
  }
  if (saved) {
    const savedRecord = saved as Record<string, unknown>;
    const mappedVoucher =
      context.kind === 'voucher' ? (voucherPreviewFromRecord(saved as Voucher) as Record<string, unknown>) : null;
    mismatches.push(
      ...proposal.changes
        .filter(({ field, after }) => {
          const actual =
            context.kind === 'promo'
              ? field.startsWith('promo.')
                ? savedRecord[field.slice(6)]
                : savedRecord[field === 'promoDiscounts' ? 'promo_discounts' : 'promo_conditions']
              : mappedVoucher?.[field];
          return !matchesExpected(after, actual);
        })
        .map(({ field }) => field),
    );
  }
  const checkResult = isObject(check) && isObject(check.result) ? check.result : null;
  if (context.kind === 'promo' && checkResult?.status === 'non-existent') {
    mismatches.push('Code check did not find the saved promo');
  }
  return { saved, check, mismatches };
}

function matchesExpected(expected: unknown, actual: unknown): boolean {
  if (Array.isArray(expected)) {
    return Array.isArray(actual) && expected.every((item) => actual.some((row) => matchesExpected(item, row)));
  }
  if (isObject(expected)) {
    return isObject(actual) && Object.entries(expected).every(([key, value]) => matchesExpected(value, actual[key]));
  }
  return expected === actual;
}
